import { useFormik } from 'formik'
import { useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as Yup from 'yup'
import { closeTaskTagsModal } from 'store/modals'
import {
  addNewTaskTag,
  newTagsSelector,
  createTaskTagAction,
  getTaskTagsAction,
  resetNewTaskTag,
} from 'store/taskTags'
import Button from 'ui/components/buttons/Button/'
import Checkbox from 'ui/components/forms/Checkbox'
import Input from 'ui/components/forms/Input'
import Icon from 'ui/components/Icon'
import { notifyError, notifySuccess } from 'ui/components/Notify'
import Palette from 'ui/components/Palette'
import {
  TAG_NAME_MAX_LENGTH,
  TAG_DESC_MAX_LENGTH,
} from 'utils/constants/taskTags'
import {
  FormStyled,
  FormLine,
  InputLine,
  CheckboxWrap,
  FormButtonWrap,
  ButtonModalWrap,
} from './styled'

const TaskTagForm = ({ setConfirmOpened }) => {
  const [formTouched, setFormTouched] = useState(false)
  const idRef = useRef(1)
  const paletteRef = useRef()

  const dispatch = useDispatch()

  const newTags = useSelector(newTagsSelector)

  const formik = useFormik({
    initialValues: {
      id: '',
      name: '',
      description: '',
      color: '',
      bg_color: '',
      is_active: true,
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .trim()
        .min(1, 'Нужно заполнить')
        .max(TAG_NAME_MAX_LENGTH, `Максимум ${TAG_NAME_MAX_LENGTH} символов`)
        .required('Нужно заполнить'),
      description: Yup.string()
        .trim()
        .min(1, 'Нужно заполнить')
        .max(TAG_DESC_MAX_LENGTH, `Максимум ${TAG_DESC_MAX_LENGTH} символов`)
        .required('Нужно заполнить'),
      color: Yup.string().required(),
      bg_color: Yup.string().required(),
    }),
    onSubmit: (values) => {
      const tagWithId = {
        ...values,
        id: `${idRef.current}`,
      }

      dispatch(addNewTaskTag(tagWithId))

      idRef.current += 1

      formik.resetForm()
      paletteRef.current?.resetColor()
    },
  })

  const handleFormSubmit = async (e) => {
    e.preventDefault()
    const errors = await formik.validateForm()

    if (errors.name) {
      notifyError('Введите название тега')
      return
    }

    if (errors.description) {
      notifyError('Введите описание тега')
      return
    }

    if (errors.color || errors.bg_color) {
      notifyError('Необходимо выбрать цвета тега')
      return
    }

    setFormTouched(false)
    formik.handleSubmit()
  }

  const saveNewTaskTagsHandler = async (e, newTags) => {
    e.preventDefault()

    const errors = await formik.validateForm()

    if ((errors.name && newTags.length === 0) || (errors.name && formTouched)) {
      notifyError('Введите название тега')
      return
    }

    if (
      (errors.description && newTags.length === 0) ||
      (errors.description && formTouched)
    ) {
      notifyError('Введите описание тега')
      return
    }

    if (
      ((errors.color || errors.bg_color) && newTags.length === 0) ||
      ((errors.color || errors.bg_color) && formTouched)
    ) {
      notifyError('Необходимо выбрать цвета тега')
      return
    }

    let sendTags

    if (Object.keys(errors).length === 0) {
      const values = formik.values
      sendTags = [...newTags, values]
    } else {
      sendTags = [...newTags]
    }

    if (sendTags.length > 0) {
      const promises = sendTags.map((tag) =>
        dispatch(
          createTaskTagAction({
            data: {
              name: tag.name,
              description: tag.description,
              color: tag.color,
              bg_color: tag.bg_color,
              is_active: tag.is_active,
            },
          })
        )
      )

      Promise.allSettled(promises).then((results) => {
        const successful = []
        const failed = []

        results.forEach((res) => {
          if (res?.value?.payload?.errorMessage) {
            failed.push(res.value.meta.arg.data)
          } else {
            successful.push(res?.value?.payload?.data)
          }
        })

        if (successful.length > 0) {
          notifySuccess(
            successful.length === 1
              ? 'Тег успешно добавлен'
              : 'Теги успешно добавлены'
          )
        }

        if (failed.length > 0) {
          failed.forEach((fail) =>
            notifyError(`${fail.name} - название уже занято`)
          )
        }

        dispatch(resetNewTaskTag())
        dispatch(getTaskTagsAction())
        dispatch(closeTaskTagsModal())
      })
    }
  }

  const resetFormHandler = (e) => {
    e.preventDefault()

    formik.resetForm()
    paletteRef.current?.resetColor()
    setFormTouched(false)
  }

  return (
    <FormStyled>
      <FormLine style={{ display: 'flex', gap: '12px' }}>
        <InputLine>
          <Input
            id="addNameTag"
            name="name"
            placeholder="Название тега"
            meta={formik.getFieldMeta('name')}
            {...formik.getFieldProps('name')}
            onFocus={() => setFormTouched(true)}
          />
          <Input
            id="addDescTag"
            name="description"
            placeholder="Чтобы запомнить зачем этот тег"
            meta={formik.getFieldMeta('description')}
            {...formik.getFieldProps('description')}
            onFocus={() => setFormTouched(true)}
          />
          <Palette
            setFieldValue={formik.setFieldValue}
            ref={paletteRef}
            onTouched={() => setFormTouched(true)}
          />
          <CheckboxWrap>
            <Checkbox
              checked={!formik.values.is_active}
              onChange={(e) =>
                formik.setFieldValue('is_active', !e.target.checked)
              }
            />
            {!formik.values.is_active ? (
              <Icon name="visibility-off" />
            ) : (
              <Icon name="visibility-on" />
            )}
          </CheckboxWrap>
          <FormButtonWrap>
            <Button
              type="submit"
              icon={<Icon name="plus-tag" />}
              onClick={(e) => handleFormSubmit(e)}
            />
          </FormButtonWrap>
        </InputLine>
        <FormButtonWrap color="red">
          <Button
            type="submit"
            theme="colorGray"
            icon={<Icon name="delete" />}
            onClick={(e) => resetFormHandler(e)}
          />
        </FormButtonWrap>
      </FormLine>

      <ButtonModalWrap>
        <Button
          title="Сохранить"
          onClick={(e) => saveNewTaskTagsHandler(e, newTags)}
        />
        <Button
          title="Отменить"
          theme="borderNone"
          onClick={(e) => {
            e.preventDefault()
            setConfirmOpened(true)
          }}
        />
      </ButtonModalWrap>
    </FormStyled>
  )
}

export default TaskTagForm
